import { APP_CONFIG, formatDate } from '../../../config/appConfig';
import type { FieldConfig } from '../../../components/generic/fieldRenderers';
import type { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import {
  NOON_ACTIVITY_OPTIONS,
  NOON_CONDITION_OPTIONS,
  NOON_GMT_OPTIONS,
} from './constants';
import {
  getInitialFormData,
  mapFormDataToPayload,
  mapItemToFormData,
  recalcSpeedDisplayFields,
} from './formUtils';
import type { NoonReport } from './types';

function num(name: string, label: string, extra?: Partial<FieldConfig>): FieldConfig {
  return { name, label, type: 'number', ...extra };
}

const speedSideEffect = { onChangeSideEffect: recalcSpeedDisplayFields };

export const moduleConfig = {
  module: {
    title: 'Noon reports',
    permissionModuleKey: 'noon_reports',
    route: {
      list: '/operations/noon-reports',
      new: '/operations/noon-reports/new',
      detail: (id: number) => `/operations/noon-reports/${id}`,
      edit: (id: number) => `/operations/noon-reports/${id}/edit`,
    },
    api: {
      list: '/noon-reports',
      detail: (id: number) => `/noon-reports/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: { placeholder: 'Search vessel, port, cargo…' },
  emptyStates: {
    noData: 'No noon reports yet.',
    noSearchResults: 'No matching noon reports.',
    noFilterResults: 'No matching noon reports.',
  },
  delete: {
    title: 'Delete noon report',
    message: 'Delete this noon report?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete noon report.',
  },
  utils: {
    formatDate: (value?: string) => (value ? formatDate(value) : ''),
    formatNumber: (value?: number | string | null, digits = 2) => {
      if (value == null || value === '') return '—';
      const n = Number(value);
      return Number.isFinite(n) ? n.toFixed(digits) : String(value);
    },
    vesselLabel: (item: NoonReport) =>
      item.vessel?.vesselName || (item.vesselId ? `Vessel #${item.vesselId}` : '—'),
  },
  detail: {
    subtitle: 'Noon report',
    notFoundTitle: 'Not found',
    notFoundMessage: 'This noon report could not be found.',
    notFoundBackText: 'Back to list',
    defaultTitle: 'Noon report',
    getTitle: (item: NoonReport) => {
      const vessel = item.vessel?.vesselName || `Vessel #${item.vesselId}`;
      const date = item.reportDate ? formatDate(String(item.reportDate).slice(0, 10)) : '';
      return date ? `${vessel} — ${date}` : vessel;
    },
  },
  form: {
    new: { title: 'New noon report', submitText: 'Create report' },
    edit: { title: 'Edit noon report', submitText: 'Save changes' },
    cancelText: 'Cancel',
    loadingText: 'Loading…',
    errorMessage: 'Could not save noon report.',
    formMaxWidth: 'max-w-6xl',
    create: async (data: Partial<NoonReport>) => {
      const { createNoonReport } = await import('../../../services/mutations');
      return createNoonReport(mapFormDataToPayload(data as Record<string, unknown>));
    },
    update: async (id: number, data: Partial<NoonReport>) => {
      const { updateNoonReport } = await import('../../../services/mutations');
      return updateNoonReport(id, mapFormDataToPayload(data as Record<string, unknown>));
    },
  },
  fields: [
    {
      name: 'vesselId',
      label: 'Vessel',
      type: 'select',
      required: true,
      placeholder: 'Select vessel',
      apiEndpoint: '/vessels?page=1&limit=500',
      valueField: 'id',
      labelField: 'vesselName',
      filterApiOptionItem: (item: unknown) => (item as { isActive?: boolean }).isActive !== false,
      validation: { required: true },
    },
    {
      name: 'reportDate',
      label: 'Report date',
      type: 'date',
      required: true,
      validation: { required: true },
    },
    {
      name: 'reportTime',
      label: 'Time',
      type: 'time',
    },
    {
      name: 'gmtOffset',
      label: 'GMT',
      type: 'select',
      options: NOON_GMT_OPTIONS,
    },
    {
      name: 'condition',
      label: 'Condition',
      type: 'select',
      options: NOON_CONDITION_OPTIONS,
    },
    {
      name: 'activities',
      label: 'Activities',
      type: 'multiselect',
      options: NOON_ACTIVITY_OPTIONS,
      placeholder: 'Select activities',
    },
    { name: 'latitude', label: 'Latitude', type: 'dms-coordinate', coordinateKind: 'latitude' },
    { name: 'longitude', label: 'Longitude', type: 'dms-coordinate', coordinateKind: 'longitude' },
    { name: 'eta', label: 'ETA', type: 'datetime' },
    { name: 'etd', label: 'ETD', type: 'datetime' },
    { name: 'nextPort', label: 'Next port of call', type: 'text' },
    num('distanceCovered', 'Distance covered (NM)'),
    num('distanceToGo', 'Distance to go (NM)'),
    { name: 'cargoType', label: 'Cargo type', type: 'text' },
    num('cargoOnBoardMt', 'Cargo on board (MT)', { step: 0.001 }),
    num('draftFwd', 'FWD', { step: 0.01 }),
    num('draftMid', 'MID', { step: 0.01 }),
    num('draftAft', 'AFT', { step: 0.01 }),
    num('presentSpeed', 'Present speed', { step: 0.01 }),
    num('avgSpeed24h', 'Avg speed (last 24h)', { step: 0.01 }),
    num('avgRpm', 'Avg RPM', { step: 0.01, ...speedSideEffect }),
    num('steamingHours', 'Steaming hours', { step: 0.01 }),
    num('pitchMm', 'Pitch (mm)', { step: 0.01, ...speedSideEffect }),
    num('avgSog', 'Avg SOG (kn)', { step: 0.01 }),
    num('avgStw', 'Avg STW (kn)', { step: 0.01, ...speedSideEffect }),
    {
      name: '_theoreticalSpeedDisplay',
      label: 'Theoretical speed (kn)',
      type: 'text',
      readOnly: true,
      helperText: 'Calculated from pitch and avg RPM',
    },
    {
      name: '_slipPercentDisplay',
      label: 'Slip (%)',
      type: 'text',
      readOnly: true,
      helperText: 'Calculated from theoretical speed and avg STW',
    },
    {
      name: 'seaStateId',
      label: 'Sea state',
      type: 'select',
      placeholder: 'Select sea state',
      helperText: 'Douglas scale',
      apiEndpoint: '/noon-lookups/sea-states?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      filterApiOptionItem: (item: unknown) => (item as { isActive?: boolean }).isActive !== false,
    },
    {
      name: 'seaCurrentDirectionId',
      label: 'Direction',
      type: 'select',
      placeholder: 'Select direction',
      apiEndpoint: '/noon-lookups/compass-directions?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      filterApiOptionItem: (item: unknown) => (item as { isActive?: boolean }).isActive !== false,
    },
    { name: 'seaCurrentForce', label: 'Force', type: 'text' },
    {
      name: 'windFromId',
      label: 'Wind from',
      type: 'select',
      placeholder: 'Select direction',
      apiEndpoint: '/noon-lookups/compass-directions?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      filterApiOptionItem: (item: unknown) => (item as { isActive?: boolean }).isActive !== false,
    },
    { name: 'windForce', label: 'Wind force', type: 'text' },
    {
      name: 'swellDirectionId',
      label: 'Direction',
      type: 'select',
      placeholder: 'Select direction',
      apiEndpoint: '/noon-lookups/compass-directions?page=1&limit=500',
      valueField: 'id',
      labelField: 'name',
      filterApiOptionItem: (item: unknown) => (item as { isActive?: boolean }).isActive !== false,
    },
    { name: 'swellForce', label: 'Force', type: 'text' },
    { name: 'weatherNotes', label: 'Weather notes', type: 'textarea', rows: 2 },
    { name: 'notes', label: 'Notes', type: 'textarea', rows: 4 },
    num('robFoMt', 'FO ROB', { step: 0.001 }),
    num('robMgoMdoMt', 'MGO/MDO ROB', { step: 0.001 }),
    num('robSystemOilL', 'System oil ROB', { step: 0.01 }),
    num('robCylinderOilL', 'Cylinder oil ROB', { step: 0.01 }),
    num('robDgSystemOilL', 'D/G system oil ROB', { step: 0.01 }),
    num('robHydrOilL', 'Hydr. oil ROB', { step: 0.01 }),
    num('robFwM3', 'FW ROB', { step: 0.001 }),
    num('robSludgesM3', 'Sludges ROB', { step: 0.001 }),
    num('robBilgesM3', 'Bilges ROB', { step: 0.001 }),
    num('consMeFoMt', 'M/E FO', { step: 0.001 }),
    num('consDgFoMt', 'D/G FO', { step: 0.001 }),
    num('consBlrFoMt', 'BLR FO', { step: 0.001 }),
    num('consIggFoMt', 'IGG FO', { step: 0.001 }),
    num('consMeMgoMdoMt', 'M/E MGO/MDO', { step: 0.001 }),
    num('consDgMgoMdoMt', 'D/G MGO/MDO', { step: 0.001 }),
    num('consBlrMgoMdoMt', 'BLR MGO/MDO', { step: 0.001 }),
    num('consIggMgoMdoMt', 'IGG MGO/MDO', { step: 0.001 }),
    num('consIncineratorMgoMdoMt', 'Incinerator MGO/MDO', { step: 0.001 }),
    num('consSystemOilL', 'System oil', { step: 0.01 }),
    num('consCylinderOilL', 'Cylinder oil', { step: 0.01 }),
    num('consDgSystemOilL', 'D/G system oil', { step: 0.01 }),
    num('consHydrOilL', 'Hydr. oil', { step: 0.01 }),
    num('consFwM3', 'FW', { step: 0.001 }),
    num('rhMeDaily', 'Main engine', { step: 0.01 }),
    num('rhMeTotal', 'Main engine (total)', { step: 0.01 }),
    num('rhAux1Daily', 'Aux 1', { step: 0.01 }),
    num('rhAux1Total', 'Aux 1 (total)', { step: 0.01 }),
    num('rhAux2Daily', 'Aux 2', { step: 0.01 }),
    num('rhAux2Total', 'Aux 2 (total)', { step: 0.01 }),
    num('rhAux3Daily', 'Aux 3', { step: 0.01 }),
    num('rhAux3Total', 'Aux 3 (total)', { step: 0.01 }),
    num('rhEmergencyAuxDaily', 'Emergency aux', { step: 0.01 }),
    num('rhEmergencyAuxTotal', 'Emergency aux (total)', { step: 0.01 }),
    num('rhBoilerDaily', 'Boiler', { step: 0.01 }),
    num('rhBoilerTotal', 'Boiler (total)', { step: 0.01 }),
    num('rhIggDaily', 'IGG', { step: 0.01 }),
    num('rhIggTotal', 'IGG (total)', { step: 0.01 }),
    num('rhIncineratorDaily', 'Incinerator', { step: 0.01 }),
    num('rhIncineratorTotal', 'Incinerator (total)', { step: 0.01 }),
  ] as FieldConfig[],
  formLayout: {
    mode: 'tabs',
    headerSections: [
      {
        id: 'report-identity',
        title: '',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: ['vesselId', 'condition', 'activities', 'reportDate', 'reportTime', 'gmtOffset'],
        fieldSpans: {
          vesselId: 'md:col-span-5',
          condition: 'md:col-span-3',
          activities: 'md:col-span-4',
          reportDate: 'md:col-span-4',
          reportTime: 'md:col-span-2',
          gmtOffset: 'md:col-span-2',
        },
      },
      {
        id: 'position',
        title: 'Position',
        gridCols: 'md:grid-cols-12',
        gridAlign: 'start',
        fields: [
          'latitude',
          'longitude',
          'eta',
          'distanceCovered',
          'distanceToGo',
          'etd',
          'nextPort',
        ],
        fieldSpans: {
          latitude: 'md:col-span-6',
          longitude: 'md:col-span-6',
          eta: 'md:col-span-6',
          distanceCovered: 'md:col-span-3',
          distanceToGo: 'md:col-span-3',
          etd: 'md:col-span-6',
          nextPort: 'md:col-span-6',
        },
      },
      {
        id: 'cargo',
        title: '',
        gridCols: 'grid-cols-2',
        gridAlign: 'start',
        className: 'pb-0 border-b-0 mb-0',
        fields: ['cargoType', 'cargoOnBoardMt'],
      },
    ],
    sections: [
      {
        id: 'details',
        title: 'Details',
        fields: [],
        subsections: [
          {
            id: 'draft',
            title: 'Draft (m)',
            className: 'md:col-span-3',
            gridCols: 'grid-cols-1',
            gridAlign: 'start',
            fields: ['draftFwd', 'draftMid', 'draftAft'],
          },
          {
            id: 'speed',
            title: 'Speed (kn)',
            className: 'md:col-span-9',
            gridCols: 'md:grid-cols-3',
            gridAlign: 'start',
            fields: [
              'presentSpeed',
              'avgSpeed24h',
              'avgRpm',
              'steamingHours',
              'pitchMm',
              'avgSog',
            ],
          },
          {
            id: 'speed-performance',
            title: '',
            className: 'md:col-span-12',
            gridCols: 'grid-cols-1 md:grid-cols-3',
            gridAlign: 'start',
            fields: ['avgStw', '_theoreticalSpeedDisplay', '_slipPercentDisplay'],
          },
          {
            id: 'weather',
            title: 'Weather conditions',
            className: 'md:col-span-12',
            fields: [],
            subsections: [
              {
                id: 'sea-state',
                title: '',
                className: 'md:col-span-3',
                gridCols: 'grid-cols-1',
                gridAlign: 'start',
                fields: ['seaStateId'],
              },
              {
                id: 'weather-notes',
                title: '',
                className: 'md:col-span-9',
                gridCols: 'grid-cols-1',
                gridAlign: 'start',
                fields: ['weatherNotes'],
              },
              {
                id: 'sea-current',
                title: 'Sea current',
                className: 'md:col-span-4',
                gridCols: 'grid-cols-1',
                gridAlign: 'start',
                fields: ['seaCurrentDirectionId', 'seaCurrentForce'],
              },
              {
                id: 'weather-wx',
                title: 'Weather (WX)',
                className: 'md:col-span-4',
                gridCols: 'grid-cols-1',
                gridAlign: 'start',
                fields: ['windFromId', 'windForce'],
              },
              {
                id: 'swell',
                title: 'Swell',
                className: 'md:col-span-4',
                gridCols: 'grid-cols-1',
                gridAlign: 'start',
                fields: ['swellDirectionId', 'swellForce'],
              },
            ],
          },
          {
            id: 'notes',
            title: 'Notes',
            className: 'md:col-span-12',
            gridCols: 'grid-cols-1',
            gridAlign: 'start',
            fields: ['notes'],
          },
        ],
      },
      {
        id: 'rob',
        title: 'ROB / Consumptions',
        fields: [],
        subsections: [
          {
            id: 'fuel',
            title: 'Fuel',
            description: 'Daily (left) and ROB (right), Mt',
            className: 'md:col-span-4',
            gridCols: 'md:grid-cols-2',
            gridAlign: 'start',
            fields: [
              'consMeFoMt',
              'robFoMt',
              'consDgFoMt',
              'consBlrFoMt',
              'consIggFoMt',
              'consMeMgoMdoMt',
              'robMgoMdoMt',
              'consDgMgoMdoMt',
              'consBlrMgoMdoMt',
              'consIggMgoMdoMt',
            ],
            fieldSpans: {
              consMeFoMt: 'md:col-span-1',
              robFoMt: 'md:col-span-1',
              consDgFoMt: 'md:col-span-1',
              consBlrFoMt: 'md:col-span-1',
              consIggFoMt: 'md:col-span-1',
              consMeMgoMdoMt: 'md:col-span-1',
              robMgoMdoMt: 'md:col-span-1',
              consDgMgoMdoMt: 'md:col-span-1',
              consBlrMgoMdoMt: 'md:col-span-1',
              consIggMgoMdoMt: 'md:col-span-1',
            },
          },
          {
            id: 'lubricants',
            title: 'Lubricants',
            description: 'Daily (left) and ROB (right), Lt',
            className: 'md:col-span-4',
            gridCols: 'md:grid-cols-2',
            gridAlign: 'start',
            fields: [
              'consSystemOilL',
              'robSystemOilL',
              'consCylinderOilL',
              'robCylinderOilL',
              'consDgSystemOilL',
              'robDgSystemOilL',
              'consHydrOilL',
              'robHydrOilL',
            ],
            fieldSpans: {
              consSystemOilL: 'md:col-span-1',
              robSystemOilL: 'md:col-span-1',
              consCylinderOilL: 'md:col-span-1',
              robCylinderOilL: 'md:col-span-1',
              consDgSystemOilL: 'md:col-span-1',
              robDgSystemOilL: 'md:col-span-1',
              consHydrOilL: 'md:col-span-1',
              robHydrOilL: 'md:col-span-1',
            },
          },
          {
            id: 'running-hours',
            title: 'Running hours',
            description: 'Daily (left) and total (right)',
            className: 'md:col-span-4',
            gridCols: 'md:grid-cols-2',
            gridAlign: 'start',
            fields: [
              'rhMeDaily',
              'rhMeTotal',
              'rhAux1Daily',
              'rhAux1Total',
              'rhAux2Daily',
              'rhAux2Total',
              'rhAux3Daily',
              'rhAux3Total',
              'rhEmergencyAuxDaily',
              'rhEmergencyAuxTotal',
              'rhBoilerDaily',
              'rhBoilerTotal',
              'rhIggDaily',
              'rhIggTotal',
              'rhIncineratorDaily',
              'rhIncineratorTotal',
            ],
            fieldSpans: {
              rhMeDaily: 'md:col-span-1',
              rhMeTotal: 'md:col-span-1',
              rhAux1Daily: 'md:col-span-1',
              rhAux1Total: 'md:col-span-1',
              rhAux2Daily: 'md:col-span-1',
              rhAux2Total: 'md:col-span-1',
              rhAux3Daily: 'md:col-span-1',
              rhAux3Total: 'md:col-span-1',
              rhEmergencyAuxDaily: 'md:col-span-1',
              rhEmergencyAuxTotal: 'md:col-span-1',
              rhBoilerDaily: 'md:col-span-1',
              rhBoilerTotal: 'md:col-span-1',
              rhIggDaily: 'md:col-span-1',
              rhIggTotal: 'md:col-span-1',
              rhIncineratorDaily: 'md:col-span-1',
              rhIncineratorTotal: 'md:col-span-1',
            },
          },
          {
            id: 'water-incinerator',
            title: 'Fresh water & other',
            className: 'md:col-span-12',
            gridCols: 'md:grid-cols-12',
            gridAlign: 'start',
            fields: [
              'consFwM3',
              'robFwM3',
              'consIncineratorMgoMdoMt',
              'robSludgesM3',
              'robBilgesM3',
            ],
            fieldSpans: {
              consFwM3: 'md:col-span-2',
              robFwM3: 'md:col-span-2',
              consIncineratorMgoMdoMt: 'md:col-span-3',
              robSludgesM3: 'md:col-span-2',
              robBilgesM3: 'md:col-span-3',
            },
          },
        ],
      },
    ],
  } as FormLayout,
};

export { getInitialFormData, mapItemToFormData };
